"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BLOCK_DATA_RETRIEVED = exports.STATUS_BATCH_VERIFIED = exports.TRANSACTION_RETRIEVED = void 0;
const index_1 = require("../../index");
const EventEmitter = require("eventemitter2");
const AeternityService_1 = __importDefault(require("../AeternityService/AeternityService"));
const CloudflareService_1 = __importDefault(require("../CloudflareService"));
const VerificationError_1 = __importDefault(require("./VerificationError"));
exports.TRANSACTION_RETRIEVED = "TRANSACTION_RETRIEVED";
exports.STATUS_BATCH_VERIFIED = "STATUS_BATCH_VERIFIED";
exports.BLOCK_DATA_RETRIEVED = "BLOCK_DATA_RETRIEVED";
/**
 * Legacy verification method. Support for older statuses
 */
const VERIFICATION_METHOD_MERKLE_TREE = "VERIFICATION_METHOD_MERKLE_TREE";
const VERIFICATION_METHOD_SHA256_BATCH = "VERIFICATION_METHOD_SHA256_BATCH";
const STATUSES_BATCH_NAME_PREFIX = "batch";
const STATUSES_MERKLE_TREE_NAME_PREFIX = "bmt";
class VerificationService extends EventEmitter {
    constructor(cdnUrl, cdnBucket, aeternityNodeUrls, aeternityCompilerUrls, aeternityContractBytecode) {
        super();
        this._aeternityService = null;
        this._cloudflareService = null;
        this._aeternityService = new AeternityService_1.default(aeternityNodeUrls, aeternityCompilerUrls, aeternityContractBytecode);
        this._cloudflareService = new CloudflareService_1.default(cdnUrl, cdnBucket);
    }
    getTransaction(hash) {
        return __awaiter(this, void 0, void 0, function* () {
            let tx;
            try {
                tx = yield this._aeternityService.getTxDataByHash(hash);
            }
            catch (e) {
                this.catchVerificationError(e, `Error getting transaction`);
                return null;
            }
            return tx;
        });
    }
    getBlockData(blockHeight) {
        return __awaiter(this, void 0, void 0, function* () {
            let blockchainHeight;
            let blockInfo;
            try {
                if (blockHeight > -1) {
                    const result = yield Promise.all([
                        this._aeternityService.getBlockchainHeight(),
                        this._aeternityService.getBlock(blockHeight),
                    ]);
                    blockchainHeight = result[0];
                    blockInfo = result[1];
                }
                else {
                    // Transactions with blockHeight = -1 considered as the ones with zero confirmations
                    // and pending to be settled in the block at latest height
                    // Original issue: https://github.com/aeternity/aeternity/pull/3550
                    blockchainHeight = yield this._aeternityService.getBlockchainHeight();
                    blockInfo = yield this._aeternityService.getBlock(blockchainHeight);
                }
            }
            catch (e) {
                this.catchVerificationError(e, `Error retrieving block`);
                return null;
            }
            return {
                blockHeight: blockInfo.height,
                timestamp: blockInfo.time,
                confirmationsAmount: blockchainHeight - blockInfo.height,
            };
        });
    }
    verifyStatusData(statusData) {
        return __awaiter(this, void 0, void 0, function* () {
            let verificationError;
            let blockData;
            let batchVerificationDetails;
            let txData;
            try {
                txData = yield this.getTransaction(statusData.transactionHash);
                this.emit(exports.TRANSACTION_RETRIEVED, txData);
                const statusBatchData = yield this.getStatusBatchDataForTx(txData);
                const { verificationMethod, items: batchItems, hash: batchHash, } = statusBatchData;
                let statusVerified;
                let statusPosition = -1;
                if (verificationMethod === VERIFICATION_METHOD_MERKLE_TREE) {
                    ({
                        verified: statusVerified,
                        statusPosition,
                    } = yield this.verifyStatusesMerkleTree(statusData.statusRaw, statusBatchData));
                }
                else if (verificationMethod === VERIFICATION_METHOD_SHA256_BATCH) {
                    ({
                        verified: statusVerified,
                        statusPosition,
                    } = yield this.verifyStatusSHA256Batch(statusData.statusRaw, statusBatchData));
                }
                batchVerificationDetails = {
                    verified: statusVerified,
                    batchSize: batchItems.length,
                    statusPosition,
                    batchHash,
                };
                this.emit(exports.STATUS_BATCH_VERIFIED, batchVerificationDetails);
                const blockRequests = yield Promise.all([
                    this.getBlockData(txData.blockHeight),
                    this._aeternityService.getMicroBlock(txData.blockHash),
                ]);
                blockData = blockRequests[0];
                const microBlockData = blockRequests[1];
                txData.time = microBlockData.time;
                this.emit(exports.BLOCK_DATA_RETRIEVED, blockData);
            }
            catch (e) {
                if (e instanceof VerificationError_1.default) {
                    verificationError = e;
                }
                else {
                    throw e;
                }
            }
            return {
                statusData,
                batchVerificationDetails,
                blockData,
                verificationError,
                txData,
            };
        });
    }
    getStatusBatchDataForTx(txData) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let verificationMethod;
            let batchItems;
            let decodedCallData;
            try {
                decodedCallData = yield this._aeternityService.decodeContractCallData((_a = txData.tx) === null || _a === void 0 ? void 0 : _a.callData);
            }
            catch (e) {
                this.catchVerificationError(e, `Error decoding contract call data`);
            }
            const { key: batchFilename, value } = decodedCallData;
            const batchHash = value;
            const lowerCaseFilename = batchFilename.toLowerCase();
            if (lowerCaseFilename.startsWith(STATUSES_MERKLE_TREE_NAME_PREFIX)) {
                verificationMethod = VERIFICATION_METHOD_MERKLE_TREE;
            }
            else if (lowerCaseFilename.startsWith(STATUSES_BATCH_NAME_PREFIX)) {
                verificationMethod = VERIFICATION_METHOD_SHA256_BATCH;
            }
            else {
                // Unrecognized verification method
                throw new VerificationError_1.default(`No corresponding verification method found for batch "${batchFilename}"`);
            }
            try {
                if (verificationMethod === VERIFICATION_METHOD_MERKLE_TREE) {
                    batchItems = yield this._cloudflareService.getMerkleTree(batchFilename);
                }
                else if (verificationMethod === VERIFICATION_METHOD_SHA256_BATCH) {
                    ({ data: batchItems } = yield this._cloudflareService.fetchFile(batchFilename));
                }
            }
            catch (e) {
                this.catchVerificationError(e, `Error obtaining batch of statuses for verification.`);
            }
            return {
                verificationMethod,
                hash: batchHash,
                items: batchItems,
            };
        });
    }
    verifyStatusSHA256Batch(status, statusesBatchData) {
        return __awaiter(this, void 0, void 0, function* () {
            const statusSha256 = (0, index_1.arrayBufferToBase64)(yield index_1.CryptoService.SHA256(status, "base64"));
            let verified = false;
            const statusPosition = statusesBatchData.items.indexOf(statusSha256) + 1;
            if (statusPosition > 0) {
                const bytesToHash = [];
                let hashesTotalByteLength = 0;
                for (const statusHash of statusesBatchData.items) {
                    const statusHashBytes = (0, index_1.base64ToArrayBuffer)(statusHash);
                    hashesTotalByteLength += statusHashBytes.byteLength;
                    bytesToHash.push(new Uint8Array(statusHashBytes));
                }
                const batchBytes = new Uint8Array(hashesTotalByteLength);
                let bytesOffset = 0;
                bytesToHash.forEach((statusHashBytes) => {
                    batchBytes.set(statusHashBytes, bytesOffset);
                    bytesOffset += statusHashBytes.byteLength;
                });
                const batchHash = (0, index_1.arrayBufferToBase64)(yield index_1.CryptoService.SHA256(batchBytes));
                verified = batchHash === statusesBatchData.hash;
            }
            else {
                throw new VerificationError_1.default(`Status not present in a batch.`);
            }
            if (!verified) {
                throw new VerificationError_1.default(`Batch of statuses not verified.`);
            }
            return {
                verified,
                statusPosition,
            };
        });
    }
    verifyStatusesMerkleTree(nodeToVerify, merkleTree) {
        return __awaiter(this, void 0, void 0, function* () {
            const leaves = yield Promise.all(merkleTree.items.map((x) => __awaiter(this, void 0, void 0, function* () { return Buffer.from(yield index_1.CryptoService.SHA256(x)); })));
            const rootHash = (yield (0, index_1.getMerkleTreeRootHash)(leaves)).toString("base64");
            const verified = rootHash === merkleTree.hash;
            if (!verified) {
                throw new VerificationError_1.default(`Merkle tree not verified.`);
            }
            const statusPosition = merkleTree.items.indexOf(nodeToVerify) + 1;
            if (statusPosition === 0) {
                throw new VerificationError_1.default(`Status not present in merkle tree.`);
            }
            return { verified, statusPosition };
        });
    }
    catchVerificationError(error, verificationFailureMessage) {
        if (error.response) {
            throw new VerificationError_1.default(verificationFailureMessage);
        }
        else {
            throw error;
        }
    }
}
VerificationService.TRANSACTION_RETRIEVED = exports.TRANSACTION_RETRIEVED;
VerificationService.STATUS_BATCH_VERIFIED = exports.STATUS_BATCH_VERIFIED;
VerificationService.BLOCK_DATA_RETRIEVED = exports.BLOCK_DATA_RETRIEVED;
exports.default = VerificationService;
